import { TextField } from '@doordash/component-fields'
import * as React from 'react'
import { US_REGEX, LOCALE_REGEX, LOCALE_NUMBERS } from './constants'

type InternationalNumberFieldProps = {
  id?: string
  accessibilityDescribedBy?: string
  max?: number
  min?: number
  value?: number | string
  placeholder?: number | string
  onChange?: (field, value) => void
  error?: boolean
  label?: string
  anchorId?: string
  locale?: string
}

class InternationalNumberField extends React.PureComponent<
  InternationalNumberFieldProps
> {
  static defaultProps = {
    ...TextField.defaultProps,
    locale: 'US',
  }

  static Sizes = TextField.Sizes

  constructor(props) {
    super(props)
    if (props.min && props.max && props.min > props.max) {
      console.warn('Min should be smaller than Max') // eslint-disable-line no-console
    }
  }

  removeNonNumbers = (value, regexToUse) => {
    const updated = value.match(regexToUse)
    return updated && updated[0]
  }

  isNumberWithinBounds = value => {
    const { min, max } = this.props

    const hasMin = min >= 0
    const hasMax = max >= 0

    return (
      (!hasMin && !hasMax) ||
      (hasMax && !hasMin && value <= max) ||
      (hasMin && !hasMax && value >= min) ||
      (hasMin && hasMax && value <= max && value >= min)
    )
  }

  handleChange = (value, event) => {
    const validValue = this.getValidValue(value)

    if (validValue !== null) {
      this.props.onChange(validValue, event)
    }
  }

  formatValue = value => {
    const newValue = this.getValidValue(value)
    return newValue === null ? '' : newValue
  }

  standardizeNumbers = value => {
    const { locale } = this.props
    return value
      .split('')
      .map(letter =>
        !Number.isNaN(parseFloat(letter))
          ? letter
          : LOCALE_NUMBERS[locale][letter]
      )
      .join('')
  }

  /**
   * getValidValue('') => ''
   * getValidValue('d') => ''
   * getValidValue('12') => '12'
   * getValidValue('12d') => '12'
   * getValidValue('9０') => '90'
   * getValidValue('120') => null as it's not in bounds
   *
   * @param {string} value a string including numbers
   * @returns {string | null} a parsed value representing a valid string of
   * numbers. Will return `null` if value passed in parameter is invalid
   * (not in the range between `min` and `max` props)
   */
  getValidValue = value => {
    const { locale } = this.props
    let stringOfNumbers
    if (typeof value === 'number') {
      stringOfNumbers = value.toString()
    } else {
      stringOfNumbers = this.removeNonNumbers(
        value,
        LOCALE_REGEX[locale] || US_REGEX
      )
    }

    // If the string has no numbers in it, we return an empty string
    // as we allow the user to clear the field.
    if (!stringOfNumbers) {
      return ''
    }

    // If the number is within bounds, we return the parsed string.
    stringOfNumbers = this.standardizeNumbers(stringOfNumbers)
    const parsedNumbers = parseFloat(stringOfNumbers)
    if (this.isNumberWithinBounds(parsedNumbers)) {
      return stringOfNumbers
    }

    // If the number is not within bounds we return `null` to indicate
    // that the value is not valid, which we'll use to prevent any update
    // of the value in the field.
    return null
  }

  render() {
    const { value, anchorId, ...rest } = this.props

    return (
      <TextField
        {...rest}
        pattern="[\d０１２３４５６７８９]*"
        value={this.formatValue(value)}
        onChange={this.handleChange}
        data-anchor-id={anchorId}
        autoComplete="off"
        isLabelHidden
      />
    )
  }
}

export default InternationalNumberField
